import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';

const Concierge = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [pickupAddress, setPickupAddress] = useState('');
  const [destination, setDestination] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [serviceTime, setServiceTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [userBookings, setUserBookings] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const pickupInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const autocompletePickup = useRef(null);
  const autocompleteDestination = useRef(null);

  // Set minimum date to today
  const minDate = new Date().toISOString().split('T')[0];

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  useEffect(() => {
    // Load Google Maps script
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBvYq6yNASqpfBkXWuUUliHe5dJmg5mlJs&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google Maps loaded');
        setTimeout(initializeAutocomplete, 100);
      };
      document.head.appendChild(script);
    } else {
      setTimeout(initializeAutocomplete, 100);
    }
  }, []);

  // Re-initialize autocomplete when inputs are mounted (for when switching between history and form)
  useEffect(() => {
    if (pickupInputRef.current && destinationInputRef.current && window.google) {
      initializeAutocomplete();
    }
  }, [showHistory]);

  useEffect(() => {
    // Fetch user bookings if logged in
    if (user) {
      fetchUserBookings();
    }
  }, [user]);

  const initializeAutocomplete = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps not loaded yet, retrying...');
      setTimeout(initializeAutocomplete, 500);
      return;
    }

    // Initialize pickup autocomplete
    if (pickupInputRef.current && !autocompletePickup.current) {
      try {
        autocompletePickup.current = new window.google.maps.places.Autocomplete(pickupInputRef.current, {
          types: ['geocode']
        });
        
        autocompletePickup.current.addListener('place_changed', () => {
          const place = autocompletePickup.current.getPlace();
          if (place.formatted_address) {
            setPickupAddress(place.formatted_address);
            setUseCurrentLocation(false);
          }
        });
        console.log('✓ Pickup autocomplete initialized');
      } catch (error) {
        console.error('Error initializing pickup autocomplete:', error);
      }
    }

    // Initialize destination autocomplete
    if (destinationInputRef.current && !autocompleteDestination.current) {
      try {
        autocompleteDestination.current = new window.google.maps.places.Autocomplete(destinationInputRef.current, {
          types: ['geocode']
        });
        
        autocompleteDestination.current.addListener('place_changed', () => {
          const place = autocompleteDestination.current.getPlace();
          if (place.formatted_address) {
            setDestination(place.formatted_address);
          }
        });
        console.log('✓ Destination autocomplete initialized');
      } catch (error) {
        console.error('Error initializing destination autocomplete:', error);
      }
    }
  };

  const fetchUserBookings = async () => {
    if (!user) return;
    
    setLoadingBookings(true);
    try {
      const { data, error } = await supabase
        .from('vip_protect_bookings')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserBookings(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      showNotification('Geolocation is not supported by your browser', 'error');
      return;
    }

    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setCurrentLocation(location);
        
        // Reverse geocode to get actual address using Google Maps API
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.lat},${location.lng}&key=AIzaSyBvYq6yNASqpfBkXWuUUliHe5dJmg5mlJs`
          );
          const data = await response.json();
          
          if (data.status === 'OK' && data.results && data.results[0]) {
            // Use the formatted address from Google Maps
            setPickupAddress(data.results[0].formatted_address);
          } else {
            setPickupAddress(`${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
          }
        } catch (error) {
          console.error('Error getting address:', error);
          setPickupAddress(`${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
        }
        
        setUseCurrentLocation(true);
        setLoadingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        showNotification('Unable to retrieve your location', 'error');
        setLoadingLocation(false);
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!(useCurrentLocation || pickupAddress) || !destination || !serviceDate || !serviceTime || !endTime) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    // Validate end time is after start time
    if (endTime <= serviceTime) {
      showNotification('End time must be after start time', 'error');
      return;
    }

    if (!user) {
      showNotification('Please login to book VIP services', 'error');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    // Calculate duration in hours
    const start = new Date(`2000-01-01T${serviceTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const durationHours = (end - start) / (1000 * 60 * 60);

    try {
      const bookingData = {
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        user_phone: user.phone || 'Not provided',
        pickup_address: pickupAddress,
        destination_address: destination,
        service_date: serviceDate,
        service_time: serviceTime,
        end_time: endTime,
        duration_hours: durationHours,
        status: 'pending'
      };

      const { data, error } = await supabase
        .from('vip_protect_bookings')
        .insert([bookingData]);

      if (error) throw error;

      showNotification(`VIP Protect booking submitted successfully! ${durationHours} hour(s) rental. We will contact you shortly to confirm.`, 'success');
      
      // Reset form
      setPickupAddress('');
      setDestination('');
      setServiceDate('');
      setServiceTime('');
      setEndTime('');
      setUseCurrentLocation(false);
      setCurrentLocation(null);
      
      // Refresh bookings
      fetchUserBookings();
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      showNotification('Error submitting booking. Please try again or contact support.', 'error');
    }
  };

  return (
    <div className="page-container">
      {/* Notification Toast */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          left: '20px',
          maxWidth: '500px',
          margin: '0 auto',
          padding: '1rem 1.5rem',
          background: notification.type === 'success' ? '#4CAF50' : notification.type === 'error' ? '#f44336' : 'var(--gold)',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{ flex: 1, fontWeight: '500' }}>{notification.message}</div>
          <button
            onClick={() => setNotification({ show: false, message: '', type: '' })}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      <div className="page-wrapper">
        <h1 className="page-title">London VIP Protect & Chauffeur Drive</h1>
        <div className="underline"></div>
        <p className="page-subtitle">Premium Chauffeur & Security Services</p>

        {/* Booking History Toggle */}
        {user && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '2rem',
            padding: '0 1rem'
          }}>
            <div style={{
              display: 'inline-flex',
              background: 'white',
              border: '2px solid var(--gold)',
              borderRadius: '12px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(205, 182, 136, 0.2)'
            }}>
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                style={{
                  padding: '0.75rem 2rem',
                  background: !showHistory ? 'var(--gold)' : 'transparent',
                  color: !showHistory ? 'black' : '#666',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                New Booking
              </button>
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                style={{
                  padding: '0.75rem 2rem',
                  background: showHistory ? 'var(--gold)' : 'transparent',
                  color: showHistory ? 'black' : '#666',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                My Bookings
              </button>
            </div>
          </div>
        )}

        {/* Booking History Section */}
        {showHistory && user && (
          <div className="card" style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>
            <h3 style={{ color: 'var(--gold)', marginBottom: '1.5rem' }}>
              My Bookings History
            </h3>
            {loadingBookings ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <LoadingSpinner />
              </div>
            ) : userBookings.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>No bookings yet. Create your first booking below!</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {userBookings.map((booking) => (
                  <div
                    key={booking.id}
                    style={{
                      padding: '1.5rem',
                      background: booking.status === 'confirmed' ? 'rgba(76, 175, 80, 0.05)' : booking.status === 'pending' ? 'rgba(205, 182, 136, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      border: `2px solid ${booking.status === 'confirmed' ? '#4CAF50' : booking.status === 'pending' ? 'var(--gold)' : '#ddd'}`,
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            background: booking.status === 'confirmed' ? '#4CAF50' : booking.status === 'pending' ? 'var(--gold)' : booking.status === 'in_progress' ? '#2196F3' : booking.status === 'completed' ? '#666' : '#f44336',
                            color: booking.status === 'pending' ? 'black' : 'white',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            textTransform: 'uppercase'
                          }}>
                            {booking.status.replace('_', ' ')}
                          </span>
                          <span style={{ fontSize: '0.9rem', color: '#666' }}>
                            {new Date(booking.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#000', marginBottom: '0.5rem' }}>
                          {new Date(booking.service_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                        <div style={{ fontSize: '0.95rem', color: '#666' }}>
                          {booking.service_time} - {booking.end_time} ({booking.duration_hours}h)
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--gold)', fontWeight: 'bold' }}>Pickup:</span>
                        <span style={{ color: '#000' }}>{booking.pickup_address}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--gold)', fontWeight: 'bold' }}>Destination:</span>
                        <span style={{ color: '#000' }}>{booking.destination_address}</span>
                      </div>
                    </div>

                    {/* Driver Information - Only shown when confirmed */}
                    {(booking.status === 'confirmed' || booking.status === 'in_progress') && booking.driver_name && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'linear-gradient(135deg, rgba(205, 182, 136, 0.1) 0%, rgba(205, 182, 136, 0.05) 100%)',
                        borderRadius: '8px',
                        border: '1px solid rgba(205, 182, 136, 0.3)'
                      }}>
                        <h4 style={{ color: 'var(--gold)', marginBottom: '0.75rem', fontSize: '1rem' }}>Your Driver Details</h4>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'start', flexWrap: 'wrap' }}>
                          {booking.driver_photo_url && (
                            <img
                              src={booking.driver_photo_url}
                              alt={booking.driver_name}
                              style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '2px solid var(--gold)',
                                flexShrink: 0
                              }}
                            />
                          )}
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#000' }}>{booking.driver_name}</div>
                            <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>Phone: {booking.driver_phone}</div>
                            {booking.vehicle_make && (
                              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                                Vehicle: {booking.vehicle_color} {booking.vehicle_make} {booking.vehicle_model} ({booking.vehicle_plate})
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {booking.notes && (
                      <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f8f8f8', borderRadius: '4px', fontSize: '0.9rem', color: '#666' }}>
                        <strong>Notes:</strong> {booking.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!showHistory && (
        <form onSubmit={handleSubmit} className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h3 style={{ color: '#000', marginBottom: '1.5rem' }}>Book Your Service</h3>

          {/* Pickup Location Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ color: '#000', marginBottom: '1rem', fontSize: '1.1rem' }}>Pickup Location</h4>
            
            <button
              type="button"
              onClick={getCurrentLocation}
              disabled={loadingLocation}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                background: useCurrentLocation ? 'var(--gold)' : 'transparent',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: useCurrentLocation ? 'black' : 'var(--gold)',
                cursor: loadingLocation ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {loadingLocation ? 'Getting Location...' : useCurrentLocation ? '✓ Using Current Location' : 'Use My Current Location'}
            </button>

            <div style={{ textAlign: 'center', margin: '1rem 0', color: '#666' }}>OR</div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                Enter Pickup Address *
              </label>
              <input
                ref={pickupInputRef}
                type="text"
                value={pickupAddress}
                onChange={(e) => setPickupAddress(e.target.value)}
                placeholder="123 Baker Street, London"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid var(--gold)',
                  borderRadius: '4px',
                  color: '#000'
                }}
              />
            </div>
          </div>

          {/* Destination */}
          <div className="form-group" style={{ marginBottom: '1.5rem', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
              Destination Address *
            </label>
            <input
              ref={destinationInputRef}
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              required
              placeholder="Heathrow Airport, London"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: '#000'
              }}
            />
          </div>

          {/* Date and Time */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                Service Date *
              </label>
              <input
                type="date"
                value={serviceDate}
                onChange={(e) => {
                  e.stopPropagation();
                  setServiceDate(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                min={minDate}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid var(--gold)',
                  borderRadius: '4px',
                  color: '#000'
                }}
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                Start Time *
              </label>
              <input
                type="time"
                value={serviceTime}
                onChange={(e) => {
                  e.stopPropagation();
                  setServiceTime(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid var(--gold)',
                  borderRadius: '4px',
                  color: '#000'
                }}
              />
            </div>
          </div>

          {/* End Time / Duration */}
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
              End Time (Duration) *
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => {
                e.stopPropagation();
                setEndTime(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: '#000'
              }}
            />
            {serviceTime && endTime && endTime > serviceTime && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(205, 182, 136, 0.1)', borderRadius: '4px', color: 'var(--gold)', fontSize: '0.9rem', fontWeight: 'bold' }}>
                ⏱ Duration: {(() => {
                  const start = new Date(`2000-01-01T${serviceTime}`);
                  const end = new Date(`2000-01-01T${endTime}`);
                  const hours = (end - start) / (1000 * 60 * 60);
                  return `${hours.toFixed(1)} hour(s)`;
                })()}
              </div>
            )}
          </div>

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '1rem',
              background: 'var(--gold)',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Request VIP Service
          </button>
        </form>
        )}

        <div className="card" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Service Features</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', textAlign: 'left' }}>
            <div>
              <strong style={{ color: '#000' }}>✓ Professional Chauffeurs</strong>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Trained & Licensed</p>
            </div>
            <div>
              <strong style={{ color: '#000' }}>✓ Luxury Vehicles</strong>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>BMW i7 Fleet</p>
            </div>
            <div>
              <strong style={{ color: '#000' }}>✓ VIP Security</strong>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Secure Transport</p>
            </div>
            <div>
              <strong style={{ color: '#000' }}>✓ 24/7 Service</strong>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Always Available</p>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Concierge;
